// api/core/auth/authInterceptor.ts
import { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { authService } from './authService';

// Extend axios config to carry our retry flag
interface RetryableRequest extends InternalAxiosRequestConfig {
    _retry?: boolean;
}

// Token refresh mechanism state
let isRefreshing = false;
let refreshSubscribers: Array<(success: boolean) => void> = [];

// Domains that should NOT receive auth tokens (map tile services etc.)
const EXCLUDED_DOMAINS = ['tiles.stadiamaps.com', 'tile.openstreetmap.org', 'tiles.openmaptiles.org'];

// Endpoints that must never trigger a refresh attempt — avoids
// the interceptor trying to refresh the refresh call itself.
const REFRESH_ENDPOINT = '/api/refresh-token';
const AUTH_ENDPOINTS = ['/api/login', REFRESH_ENDPOINT];

const shouldExcludeFromAuth = (url: string | undefined): boolean => {
    if (!url) return false;
    try {
        const urlObj = new URL(url);
        return EXCLUDED_DOMAINS.some((domain) => urlObj.hostname.includes(domain));
    } catch {
        return false;
    }
};

const isAuthEndpoint = (url = ''): boolean => AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

const subscribeToRefresh = (callback: (success: boolean) => void): void => {
    refreshSubscribers.push(callback);
};

const onRefreshed = (success: boolean): void => {
    refreshSubscribers.forEach((callback) => callback(success));
    refreshSubscribers = [];
};

const forceLogout = (reason = 'session_expired'): void => {
    isRefreshing = false;
    refreshSubscribers = [];
    if (reason === 'session_expired') {
        sessionStorage.setItem('auth_message', 'Your session has expired. Please log in again.');
    }
    // Cannot dispatch to React from here — fire an event instead
    window.dispatchEvent(new CustomEvent('auth:force-logout', { detail: { reason } }));
};

export const createAuthInterceptors = (axiosInstance: AxiosInstance): AxiosInstance => {
    // No request interceptor needed — cookies are sent automatically via withCredentials: true

    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config as RetryableRequest | undefined;
            const status = error.response?.status as number | undefined;
            const detail = error.response?.data?.detail as string | undefined;

            if (!originalRequest) {
                return Promise.reject(error);
            }

            if (shouldExcludeFromAuth(originalRequest.url)) {
                return Promise.reject(error);
            }

            // 403 Forbidden — token is valid but user lacks permission.
            // Refreshing won't help, pass through for the application to handle.
            if (status === 403) {
                return Promise.reject(error);
            }

            // If the auth/refresh endpoint itself returned 401, the session is
            // unrecoverable. Redirect to login — do not retry.
            if (status === 401 && isAuthEndpoint(originalRequest.url)) {
                forceLogout(detail === 'session_expired' ? 'session_expired' : 'auth_failure');
                return Promise.reject(error);
            }

            // 401 on a normal endpoint — attempt a token refresh.
            if (status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;

                if (isRefreshing) {
                    // Queue this request to retry once the in-flight refresh resolves.
                    return new Promise((resolve, reject) => {
                        subscribeToRefresh((success) => {
                            if (success) {
                                resolve(axiosInstance(originalRequest));
                            } else {
                                reject(error);
                            }
                        });
                    });
                }

                isRefreshing = true;

                try {
                    await authService.refreshToken();
                    isRefreshing = false;
                    onRefreshed(true);
                    return axiosInstance(originalRequest);
                } catch (refreshError) {
                    const refreshDetail = (refreshError as { response?: { data?: { detail?: string } } }).response?.data
                        ?.detail;
                    onRefreshed(false);
                    forceLogout(refreshDetail === 'session_expired' ? 'session_expired' : 'auth_failure');
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    return axiosInstance;
};
